import { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  IressButton,
  IressInline,
  IressModal,
} from '@iress-oss/ids-components';
import { NavigableError } from '@/lib/errors/NavigableError';
import { translateError } from '@/lib/errors/translateError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Button that navigates to a route via TanStack Router.
 * Extracted so the class-based ErrorBoundary can use the `useNavigate` hook.
 */
function NavigateButton({
  to,
  search,
  label,
  onNavigate,
}: {
  to: string;
  search?: Record<string, unknown>;
  label: string;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  return (
    <IressButton
      mode="primary"
      onClick={async () => {
        await navigate({ to, search });
        onNavigate();
      }}
    >
      {label}
    </IressButton>
  );
}

/**
 * Error Boundary component using IDS components.
 * Translates API, Zod, and JS errors into user-friendly messages.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to monitoring service (handled by Hydrofoil)
    console.error('Error caught by boundary:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <IressModal
          status="danger"
          show={this.state.hasError}
          heading="Something went wrong"
          footer={
            <IressInline horizontalAlign="center" mt="-spacing.6">
              <NavigateButton
                to={
                  this.state.error instanceof NavigableError
                    ? this.state.error.to
                    : '/'
                }
                search={
                  this.state.error instanceof NavigableError
                    ? this.state.error.search
                    : undefined
                }
                label={
                  this.state.error instanceof NavigableError
                    ? this.state.error.buttonLabel
                    : 'Go home'
                }
                onNavigate={this.handleReset}
              />
            </IressInline>
          }
          size="sm"
          noCloseButton
        >
          {translateError(this.state.error)}
        </IressModal>
      );
    }

    return this.props.children;
  }
}
